/**
 * Your account.
 *
 * Everything personal lives here: who you are, your password, how the app looks,
 * and what this particular device is holding. Before this page those were
 * scattered across a sidebar menu and a mobile drawer, which meant two places to
 * keep in step and no obvious home for anything new.
 *
 * The division that matters on this page is yours versus your administrator's.
 * Name is yours. Email and role are not, and the page says so rather than
 * showing a disabled input and leaving people to guess why.
 */
import { useEffect, useState } from 'react';
import {
  BadgeCheck,
  Check,
  CloudOff,
  Database,
  LogOut,
  Monitor,
  Moon,
  RefreshCw,
  RotateCcw,
  ShieldCheck,
  Sun,
  UserRound,
} from 'lucide-react';
import { ApiClientError, ROLE_DESCRIPTIONS, ROLE_LABELS } from '@mouldtrack/shared';
import { Page } from '../../components/layout/Page.js';
import { Button } from '../../components/ui/button.js';
import { Badge, Card, CardBody, CardHeader, CardTitle } from '../../components/ui/primitives.js';
import { ConfirmDialog } from '../../components/ui/dialog.js';
import { Field, Input } from '../../components/ui/field.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useSafeSignOut } from '../../auth/SafeSignOut.js';
import { updateUser } from '../../auth/session.js';
import { isNetworkError, updateProfile } from '../../api/client.js';
import { useSyncState } from '../../sync/useSync.js';
import { resetAndResync, syncNow } from '../../sync/engine.js';
import { useTheme, type Theme } from '../../lib/theme.js';
import { formatBytes, formatRelative, pluralise } from '../../lib/format.js';
import { cn } from '../../lib/cn.js';
import { ChangePasswordCard } from './ChangePasswordCard.js';

export function ProfilePage() {
  const { user } = useAuth();
  if (!user) return null;

  return (
    <Page title="Your account" description="Your details, password, appearance and this device.">
      <div className="flex max-w-3xl flex-col gap-4">
        <IdentityCard />
        <ProfileDetailsCard />
        <ChangePasswordCard />
        <AppearanceCard />
        <DeviceCard />
        <SignOutCard />
      </div>
    </Page>
  );
}

// --- identity header ---

function IdentityCard() {
  const { user } = useAuth();
  if (!user) return null;

  return (
    <Card>
      <CardBody className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <Avatar name={user.name} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-lg font-semibold text-foreground">{user.name}</p>
          <p className="truncate text-sm text-muted-foreground">{user.email}</p>
        </div>
        <div className="sm:max-w-xs sm:text-right">
          <Badge tone="primary" className="text-xs">
            <ShieldCheck className="size-3" aria-hidden />
            {ROLE_LABELS[user.role]}
          </Badge>
          <p className="mt-1.5 text-xs text-muted-foreground">{ROLE_DESCRIPTIONS[user.role]}</p>
        </div>
      </CardBody>
    </Card>
  );
}

function Avatar({ name }: { name: string }) {
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0] ?? '')
    .join('')
    .toUpperCase();
  return (
    <span
      aria-hidden
      className="grid size-14 shrink-0 place-items-center rounded-full bg-primary-subtle text-lg font-semibold text-primary"
    >
      {initials}
    </span>
  );
}

// --- editable details ---

function ProfileDetailsCard() {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name ?? '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setName(user?.name ?? ''), [user?.name]);
  useEffect(() => {
    if (!saved) return;
    const t = setTimeout(() => setSaved(false), 4000);
    return () => clearTimeout(t);
  }, [saved]);

  if (!user) return null;
  const dirty = name.trim() !== user.name && name.trim().length > 0;

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const updated = await updateProfile({ name: name.trim() });
      // Keep the shell's greeting and avatar in step without a reload.
      updateUser(updated);
      setSaved(true);
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else if (isNetworkError(err)) setError('No connection. Your name is stored on the server.');
      else setError('Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserRound className="size-4 text-muted-foreground" aria-hidden />
          Details
        </CardTitle>
        {saved ? (
          <span className="flex items-center gap-1 text-xs font-medium text-success">
            <Check className="size-3.5" aria-hidden /> Saved
          </span>
        ) : null}
      </CardHeader>
      <CardBody>
        <form onSubmit={save} className="flex max-w-md flex-col gap-4">
          <Field
            label="Display name"
            required
            hint="Shown on scans, transfers and anything else you record."
          >
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required />
          </Field>

          <ReadOnlyRow
            label="Email"
            value={user.email}
            reason="This is your sign-in name. An administrator can change it on the People screen."
          />
          <ReadOnlyRow
            label="Role"
            value={ROLE_LABELS[user.role]}
            reason="Roles decide what you can see and do, so only an administrator can change them."
          />

          {error ? (
            <p role="alert" className="rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger">
              {error}
            </p>
          ) : null}

          <div>
            <Button type="submit" variant="primary" loading={saving} disabled={!dirty}>
              Save changes
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

/**
 * A value you cannot edit, shown with the reason.
 *
 * A greyed-out input would look like a bug or a permissions failure; plain text
 * plus one line of explanation is honest and takes the same room.
 */
function ReadOnlyRow({ label, value, reason }: { label: string; value: string; reason: string }) {
  return (
    <div className="rounded-lg border border-border bg-surface-sunken px-3 py-2.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium text-foreground">{label}</span>
        <span className="truncate text-sm text-muted-foreground">{value}</span>
      </div>
      <p className="mt-1 text-xs text-subtle-foreground">{reason}</p>
    </div>
  );
}

// --- appearance ---

function AppearanceCard() {
  const [theme, setTheme] = useTheme();
  const options: Array<{ value: Theme; label: string; hint: string; icon: typeof Sun }> = [
    { value: 'light', label: 'Light', hint: 'Bright screens, daylight', icon: Sun },
    { value: 'dark', label: 'Dark', hint: 'Low light, night shift', icon: Moon },
    { value: 'system', label: 'System', hint: 'Follow the device', icon: Monitor },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Appearance</CardTitle>
        <span className="text-xs text-muted-foreground">Applies to this device only</span>
      </CardHeader>
      <CardBody>
        <div role="radiogroup" aria-label="Theme" className="grid gap-2 sm:grid-cols-3">
          {options.map((o) => {
            const Icon = o.icon;
            const active = theme === o.value;
            return (
              <button
                key={o.value}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setTheme(o.value)}
                className={cn(
                  'flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors',
                  active
                    ? 'border-primary bg-primary-subtle'
                    : 'border-border hover:border-border-strong hover:bg-surface-sunken',
                )}
              >
                <Icon
                  className={cn(
                    'size-4 shrink-0',
                    active ? 'text-primary' : 'text-muted-foreground',
                  )}
                  aria-hidden
                />
                <span className="min-w-0">
                  <span
                    className={cn(
                      'block text-sm font-medium',
                      active ? 'text-primary' : 'text-foreground',
                    )}
                  >
                    {o.label}
                  </span>
                  <span className="block text-xs text-muted-foreground">{o.hint}</span>
                </span>
                {active ? (
                  <Check className="ml-auto size-4 shrink-0 text-primary" aria-hidden />
                ) : null}
              </button>
            );
          })}
        </div>
      </CardBody>
    </Card>
  );
}

// --- device ---

/**
 * What this device is holding.
 *
 * In an offline-first app the honest answer to "is my work safe?" is a property
 * of the device, not the account, so it belongs on the profile rather than only
 * in a status popover.
 */
function DeviceCard() {
  const sync = useSyncState();
  const [usage, setUsage] = useState<{ used: number; quota: number } | null>(null);
  const [resetting, setResetting] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  useEffect(() => {
    // Not available in every browser, and it throws in some private modes.
    if (!navigator.storage?.estimate) return;
    navigator.storage
      .estimate()
      .then((e) => setUsage({ used: e.usage ?? 0, quota: e.quota ?? 0 }))
      .catch(() => setUsage(null));
  }, [sync.lastPullAt]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="size-4 text-muted-foreground" aria-hidden />
          This device
        </CardTitle>
        {sync.online ? (
          <Badge tone="success">
            <BadgeCheck className="size-3" aria-hidden /> Online
          </Badge>
        ) : (
          <Badge tone="warning">
            <CloudOff className="size-3" aria-hidden /> Offline
          </Badge>
        )}
      </CardHeader>
      <CardBody>
        <dl className="divide-y divide-border text-sm">
          <Row label="Last synced">{formatRelative(sync.lastPullAt)}</Row>
          <Row label="Waiting to send">
            {sync.pending === 0 ? (
              <span className="text-muted-foreground">Nothing — everything is saved</span>
            ) : (
              <Badge tone="info">{pluralise(sync.pending, 'change')}</Badge>
            )}
          </Row>
          {sync.failed > 0 ? (
            <Row label="Rejected">
              <Badge tone="danger">{pluralise(sync.failed, 'change')}</Badge>
            </Row>
          ) : null}
          {sync.conflicts > 0 ? (
            <Row label="Conflicts">
              <Badge tone="warning">{pluralise(sync.conflicts, 'row')}</Badge>
            </Row>
          ) : null}
          {usage ? <Row label="Stored on device">{formatBytes(usage.used)}</Row> : null}
        </dl>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => void syncNow()} disabled={!sync.online}>
            <RefreshCw className="size-4" /> Sync now
          </Button>
          <Button variant="ghost" onClick={() => setConfirmReset(true)} disabled={!sync.online}>
            <RotateCcw className="size-4" /> Re-download everything
          </Button>
        </div>

        <p className="mt-3 text-xs text-muted-foreground">
          MouldTrack keeps a copy of the catalog on this device so it works without a connection.
          Anything you change is saved here first and sent automatically when you are back online.
        </p>
      </CardBody>

      <ConfirmDialog
        open={confirmReset}
        onOpenChange={setConfirmReset}
        title="Re-download everything?"
        description={
          sync.pending > 0
            ? `This device still has ${pluralise(sync.pending, 'change')} waiting to send. Sync first, or those changes are lost.`
            : 'The local copy is cleared and downloaded again from the server. Useful if something looks out of date.'
        }
        confirmLabel="Re-download"
        destructive={sync.pending > 0}
        loading={resetting}
        onConfirm={() => {
          setResetting(true);
          void resetAndResync().finally(() => {
            setResetting(false);
            setConfirmReset(false);
          });
        }}
      />
    </Card>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium">{children}</dd>
    </div>
  );
}

// --- sign out ---

function SignOutCard() {
  const signOut = useSafeSignOut();

  return (
    <Card>
      <CardBody className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">Sign out</p>
          <p className="text-xs text-muted-foreground">
            Clears the catalog copy from this device, so a shared phone leaks nothing to the next
            shift. Anything not yet sent is sent first.
          </p>
        </div>
        <Button variant="secondary" onClick={signOut.request} loading={signOut.busy}>
          <LogOut className="size-4" /> Sign out
        </Button>
      </CardBody>
      {signOut.dialog}
    </Card>
  );
}
